import { ErrorMessage, Field, Form, Formik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import ErrorMessageComponent from '../components/ErrorMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import MobileModal from '../components/MobileModal';
import { PermissionButton } from '../components/PermissionButton';
import { PageHeader, TableActions } from '../components/ui';
import { ExpandChevron, ExpandableRow } from '../components/ui/ExpandableRow';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { ExcelColumn, exportToExcel } from '../utils/exportToExcel';

// --- Redefined Nested Interfaces based on API responses --- 
interface Vineyard {
  id: number;
  name: string;
  location?: string;
  planted_variety?: string;
  planting_date?: string;
  acreage?: string;
  brix?: string;
  ph?: string;
  ta?: string;
  sg?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface Winemaker {
  id: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  role_id?: number;
  active?: boolean;
  last_login?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface WineBatch {
  id: number;
  batch_id: string;
  vineyard_id: number;
  batch_name: string;
  wine_type?: string;
  grape_variety?: string;
  start_date?: string;
  end_date?: string | null;
  status: string;
  weight?: string;
  net_liquid_weight?: string;
  final_brix?: string;
  final_ph?: string;
  final_ta?: string;
  final_sg?: string;
  bisulfite?: string;
  tartaric_acid?: string;
  yeast_strain?: string;
  volume?: string;
  volume_unit?: string;
  responsible_user_id?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  vineyard?: Vineyard;
  winemaker?: Winemaker;
}

interface Vessel {
  id: number;
  vessel_id: string; // snake_case identifier
  type?: string;
  capacity?: string; // string from JSON
  capacity_unit?: string;
  material?: string;
  location?: string;
  vintage?: number;
  toast?: string;
  cooperage?: string;
  usage_type?: string; // snake_case
  status?: string;
  notes?: string;
  createdAt?: string; // camelCase
  updatedAt?: string; // camelCase
}
// --- End Redefined Nested Interfaces ---

// Corrected FermentationProcess interface
export interface FermentationProcess {
  id: number;
  batch_id: number; // snake_case
  vessel_id: number; // snake_case
  start_date: string; // snake_case
  end_date?: string | null; // snake_case
  initial_brix?: string; // snake_case, string from JSON
  status: string;
  press_date?: string | null; // snake_case
  yeast_type?: string; // snake_case
  yeast_volume?: string; // snake_case
  notes?: string;
  createdAt: string; // camelCase
  updatedAt: string; // camelCase
  // Use corrected nested interfaces
  wineBatch?: WineBatch;
  vessel?: Vessel;
}

// Form values match API fields for POST/PUT
interface FermentationFormValues {
  batch_id: number;
  vessel_id: number;
  start_date: string;
  end_date?: string | null; // Allow null for optional dates
  initial_brix?: number | string; // Allow number for input, handle conversion if needed
  status: string;
  press_date?: string | null; // Allow null for optional dates
  yeast_type?: string;
  yeast_volume?: string;
  notes?: string;
}

// Mapping function
const mapApiDataToFermentation = (apiData: any): FermentationProcess => {
  console.log('Raw API data being mapped:', apiData);

  // Basic mapping for top-level fields
  const mapped: FermentationProcess = {
    id: apiData.id,
    batch_id: apiData.batchId || apiData.batch_id,
    vessel_id: apiData.vesselId || apiData.vessel_id,
    start_date: apiData.startDate || apiData.start_date,
    end_date: apiData.endDate || apiData.end_date,
    initial_brix: apiData.initialBrix || apiData.initial_brix, // Keep as string from API
    status: apiData.status || 'Unknown',
    press_date: apiData.pressDate || apiData.press_date,
    yeast_type: apiData.yeastType || apiData.yeast_type,
    yeast_volume: apiData.yeastVolume || apiData.yeast_volume,
    notes: apiData.notes,
    createdAt: apiData.createdAt,
    updatedAt: apiData.updatedAt,
    wineBatch: undefined, // Initialize nested objects
    vessel: undefined
  };

  // Map nested wineBatch from the structure in the example JSON
  if (apiData.wineBatch) {
    mapped.wineBatch = {
      id: apiData.wineBatch.batchId,
      batch_id: apiData.wineBatch.batchId || apiData.wineBatch.batch_id || `#${apiData.wineBatch.id}`,
      vineyard_id: apiData.wineBatch.vineyardId,
      batch_name: apiData.wineBatch.batchName || apiData.wineBatch.batch_name || 'Unnamed Batch',
      wine_type: apiData.wineBatch.wineType,
      grape_variety: apiData.wineBatch.grapeVariety,
      start_date: apiData.wineBatch.startDate,
      end_date: apiData.wineBatch.endDate,
      status: apiData.wineBatch.status || 'Unknown',
      weight: apiData.wineBatch.weight,
      net_liquid_weight: apiData.wineBatch.net_liquid_weight,
      final_brix: apiData.wineBatch.final_brix,
      final_ph: apiData.wineBatch.final_ph,
      final_ta: apiData.wineBatch.final_ta,
      final_sg: apiData.wineBatch.final_sg,
      bisulfite: apiData.wineBatch.bisulfite,
      tartaric_acid: apiData.wineBatch.tartaric_acid,
      yeast_strain: apiData.wineBatch.yeast_strain,
      volume: apiData.wineBatch.volume,
      volume_unit: apiData.wineBatch.volume_unit,
      responsible_user_id: apiData.wineBatch.responsible_user_id,
      notes: apiData.wineBatch.notes,
      createdAt: apiData.wineBatch.createdAt,
      updatedAt: apiData.wineBatch.updatedAt,
      // Further nested objects within wineBatch
      vineyard: apiData.wineBatch.vineyard ? { ...apiData.wineBatch.vineyard } : undefined
    };
  }

  // Map nested vessel from the structure in the example JSON
  if (apiData.vessel) {
    mapped.vessel = {
      id: apiData.vessel.id,
      vessel_id: apiData.vessel.vesselId || apiData.vessel.vessel_id,
      type: apiData.vessel.type,
      capacity: apiData.vessel.capacity,
      capacity_unit: apiData.vessel.capacity_unit,
      material: apiData.vessel.material,
      location: apiData.vessel.location,
      vintage: apiData.vessel.vintage,
      toast: apiData.vessel.toast,
      cooperage: apiData.vessel.cooperage,
      usage_type: apiData.vessel.usage_type,
      status: apiData.vessel.status,
      notes: apiData.vessel.notes,
      createdAt: apiData.vessel.createdAt,
      updatedAt: apiData.vessel.updatedAt
    };
  }

  console.log('Mapped fermentation:', mapped);
  return mapped;
};

// Yup schema matches form values
const FermentationSchema = Yup.object().shape({
  batch_id: Yup.number().required('Wine batch is required').positive().integer(),
  vessel_id: Yup.number().required('Vessel is required').positive().integer(),
  start_date: Yup.date().required('Start date is required'),
  end_date: Yup.date().nullable()
    .min(Yup.ref('start_date'), "End date can't be before start date"),
  initial_brix: Yup.number().nullable().typeError('Must be a number')
    .min(0, 'Cannot be negative').max(50, 'Seems too high'),
  status: Yup.string().required('Status is required'),
  press_date: Yup.date().nullable()
    .min(Yup.ref('start_date'), "Press date can't be before start date"),
  yeast_type: Yup.string().nullable(),
  yeast_volume: Yup.string().nullable(),
  notes: Yup.string().nullable()
});

const Fermentation: React.FC<{ tabVisible?: boolean }> = ({ tabVisible = true }) => {
  const { user } = useAuth();
  const isAdmin = (user?.Role?.name || (user as any)?.role?.name || '').toLowerCase() === 'admin';
  const [fermentations, setFermentations] = useState<FermentationProcess[]>([]);
  const [wineBatches, setWineBatches] = useState<any[]>([]);
  const [vessels, setVessels] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingFermentation, setEditingFermentation] = useState<FermentationProcess | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [expandedFermentations, setExpandedFermentations] = useState<number[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [fermentationsRes, batchesRes, vesselsRes] = await Promise.all([
          api.get('/api/fermentation'),
          api.get('/api/batch'), // Keep fetching batches for dropdown
          api.get('/api/vessel') // Keep fetching vessels for dropdown
        ]);

        // Log raw API response to see its structure
        console.log('Raw fermentation API response:', fermentationsRes.data);
        console.log('Raw batches API response:', batchesRes.data);
        console.log('Raw vessels API response:', vesselsRes.data);

        // Normalize vessel data to ensure consistent property names
        const normalizedVessels = vesselsRes.data.map((vessel: any) => ({
          ...vessel,
          // Ensure we always have these properties consistently named
          usageType: vessel.usageType || vessel.usage_type,
          vesselId: vessel.vesselId || vessel.vessel_id,
          vesselType: vessel.vesselType || vessel.type,
          status: vessel.status
        }));

        console.log('Normalized vessels data:', normalizedVessels);

        // Use the mapping function - handle both array and single object responses
        const fermentationData = Array.isArray(fermentationsRes.data)
          ? fermentationsRes.data
          : [fermentationsRes.data];

        const transformedFermentations = fermentationData.map(mapApiDataToFermentation);

        console.log('Transformed fermentations:', transformedFermentations);

        setFermentations(transformedFermentations);
        // Batches and Vessels states are likely used for form dropdowns
        setWineBatches(batchesRes.data);

        // Debug batches data structure
        console.log('Wine batches for dropdown:', batchesRes.data);
        if (batchesRes.data.length > 0) {
          console.log('First batch structure:', batchesRes.data[0]);
          console.log('First batch fields:', Object.keys(batchesRes.data[0]));
        }

        // Store normalized vessels
        setVessels(normalizedVessels);

        // Debug vessel info
        console.log('Total vessels count:', normalizedVessels.length);

        const fermentationVessels = normalizedVessels.filter(
          (vessel: any) => vessel.usageType === 'Fermentation' || vessel.usageType === 'Both'
        );
        console.log('Fermentation vessels count:', fermentationVessels.length);
        console.log('Fermentation vessels:', fermentationVessels);

        const emptyVessels = normalizedVessels.filter(
          (vessel: any) => vessel.status === 'Empty'
        );
        console.log('Empty vessels count:', emptyVessels.length);
        console.log('Empty vessels:', emptyVessels);

        const emptyFermentationVessels = normalizedVessels.filter(
          (vessel: any) =>
            (vessel.usageType === 'Fermentation' || vessel.usageType === 'Both') &&
            vessel.status === 'Empty'
        );
        console.log('Empty fermentation vessels count:', emptyFermentationVessels.length);
        console.log('Empty fermentation vessels:', emptyFermentationVessels);

      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (tabVisible) fetchData();
  }, [tabVisible]);

  // Calculate available vessels based on active fermentations and vessel status
  const availableVessels = useMemo(() => {
    console.log('Recalculating available vessels');

    // First, filter vessels that can be used for fermentation (by usage_type)
    const fermentationVessels = vessels.filter(
      vessel => vessel.usageType === 'Fermentation' || vessel.usageType === 'Both'
    );

    console.log('Filtered fermentation vessels:', fermentationVessels);

    // Get IDs of vessels used in any active fermentation
    const activeVesselIds = new Set(
      fermentations
        .filter(f => f.status === 'Active')
        .map(f => f.vessel_id)
    );

    console.log('Active vessel IDs:', Array.from(activeVesselIds));

    // If editing, find the vessel currently assigned to this fermentation
    const currentEditingVessel = editingFermentation
      ? vessels.find(v => v.id === editingFermentation.vessel_id)
      : null;

    console.log('Current editing vessel:', currentEditingVessel);

    // Now filter for available vessels
    const filteredList = fermentationVessels.filter(vessel => {
      // If we're editing a fermentation, always include its current vessel
      if (editingFermentation && vessel.id === editingFermentation.vessel_id) {
        return true;
      }

      // Otherwise, only include vessels that are Empty (not In Use)
      return vessel.status !== 'In Use';
    });

    console.log('Filtered available vessels:', filteredList);

    // Ensure the currentEditingVessel is included if it exists (handles edge cases)
    if (currentEditingVessel && !filteredList.some(v => v.id === currentEditingVessel.id)) {
      filteredList.push(currentEditingVessel);
      console.log('Added current editing vessel to list');
    }

    console.log('Final available vessels:', filteredList.map(v => ({
      id: v.id,
      vessel_id: v.vesselId || v.vessel_id,
      status: v.status,
      usageType: v.usageType || v.usage_type
    })));

    return filteredList;

  }, [fermentations, vessels, editingFermentation]);

  console.log('All suitable vessels:', vessels);
  console.log('Active Fermentations:', fermentations.filter(f => f.status === 'Active'));
  console.log('Available Vessels for dropdown:', availableVessels);

  const openModal = (fermentation: FermentationProcess | null = null) => {
    console.log('Opening modal with fermentation data:', JSON.stringify(fermentation, null, 2));
    if (fermentation) {
      console.log('Date fields:', {
        startDate: fermentation.start_date,
        endDate: fermentation.end_date,
        pressDate: fermentation.press_date
      });
    }
    setEditingFermentation(fermentation);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingFermentation(null);
  };

  const toggleExpandFermentation = (fermentationId: number) => {
    setExpandedFermentations(prev =>
      prev.includes(fermentationId)
        ? prev.filter(id => id !== fermentationId)
        : [...prev, fermentationId]
    );
  };

  const handleSubmit = async (values: FermentationFormValues, { resetForm, setSubmitting }: any) => {
    try {
      setError(null); // Clear previous errors
      let updatedFermentation: FermentationProcess;

      // Log the raw form values for debugging
      console.log("Raw form values:", values);

      // Helper function to format date as ISO8601 string
      const formatDateForApi = (dateStr: string | null | undefined): string | null => {
        if (!dateStr || dateStr === '') return null;
        try {
          // The date input gives us YYYY-MM-DD, but server expects ISO8601
          // Add time component to ensure it passes validation
          const [year, month, day] = dateStr.split('-').map(Number);

          // Create a date object with the date at noon UTC to avoid timezone issues
          const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

          if (isNaN(date.getTime())) {
            console.error("Invalid date:", dateStr);
            return null;
          }

          // Format as ISO8601 string
          return date.toISOString();
        } catch (e) {
          console.error("Error formatting date:", e);
          return null;
        }
      };

      // Format dates properly for the API
      const formattedStartDate = formatDateForApi(values.start_date);
      const formattedEndDate = formatDateForApi(values.end_date);
      const formattedPressDate = formatDateForApi(values.press_date);

      console.log("Formatted dates for API:", {
        start: formattedStartDate,
        end: formattedEndDate,
        press: formattedPressDate
      });

      // Ensure start date is present and valid
      if (!formattedStartDate) {
        setError("Invalid start date format");
        setSubmitting(false);
        return;
      }

      // Map form values to match exactly what server expects
      const apiValues = {
        batch_id: Number(values.batch_id),    // Use snake_case as required by server
        vessel_id: Number(values.vessel_id),  // Use snake_case as required by server
        startDate: formattedStartDate,        // Keep camelCase for date fields
        endDate: formattedEndDate,            // Keep camelCase for date fields
        initialBrix: values.initial_brix !== undefined && values.initial_brix !== null && values.initial_brix !== ''
          ? String(values.initial_brix)
          : null,
        status: values.status,
        pressDate: formattedPressDate,        // Keep camelCase for date fields
        yeast_type: values.yeast_type || null,
        yeast_volume: values.yeast_volume || null,
        notes: values.notes || ''
      };

      // Validate required fields are present
      if (!apiValues.startDate) {
        setError("Start date is required");
        setSubmitting(false);
        return;
      }

      // Clean payload: remove null/undefined fields
      Object.keys(apiValues).forEach((key) => {
        if ((apiValues as any)[key] === null || (apiValues as any)[key] === undefined) {
          delete (apiValues as any)[key];
        }
      });

      console.log("Submitting API Values:", apiValues);

      if (editingFermentation) {
        // Update existing fermentation
        console.log('Sending update request with payload:', JSON.stringify(apiValues, null, 2));
        try {
          const updateResponse = await api.put(`/api/fermentation/${editingFermentation.id}`, apiValues);
          console.log('Update response:', updateResponse.data);
          updatedFermentation = await getUpdatedFermentation(editingFermentation.id);
          setFermentations(fermentations.map(f => (f.id === editingFermentation.id ? updatedFermentation : f)));

          // If vessel has changed, update both old and new vessel statuses
          if (values.vessel_id !== editingFermentation.vessel_id) {
            setVessels(prevVessels =>
              prevVessels.map(v => {
                if (v.id === Number(values.vessel_id)) {
                  // New vessel - mark as In Use
                  return { ...v, status: 'In Use' };
                } else if (v.id === editingFermentation.vessel_id) {
                  // Old vessel - mark as Empty
                  return { ...v, status: 'Empty' };
                }
                return v;
              })
            );
          }

          // If status is changed to 'Completed', mark the vessel as Empty
          if (values.status === 'Completed' && editingFermentation.status !== 'Completed') {
            setVessels(prevVessels =>
              prevVessels.map(v =>
                v.id === Number(values.vessel_id)
                  ? { ...v, status: 'Empty' }
                  : v
              )
            );
          }

          setSuccessMessage('Fermentation updated successfully!');
        } catch (updateError: any) {
          console.error('Update error response:', updateError.response?.data);
          throw updateError;
        }
      } else {
        // Create new fermentation
        console.log('Sending create request with payload:', JSON.stringify(apiValues, null, 2));
        try {
          console.log('Final payload being sent:', apiValues);
          const createResponse = await api.post('/api/fermentation', apiValues);
          console.log('Create response:', createResponse.data);

          if (createResponse.data && createResponse.data.id) {
            updatedFermentation = await getUpdatedFermentation(createResponse.data.id);
            // Add to the beginning of the list
            setFermentations(prev => [updatedFermentation, ...prev]);

            // Update vessel status in the local state
            setVessels(prevVessels =>
              prevVessels.map(v =>
                v.id === Number(values.vessel_id)
                  ? { ...v, status: 'In Use' }
                  : v
              )
            );

            setSuccessMessage('Fermentation created successfully!');
          } else {
            throw new Error('Failed to create fermentation: No ID returned from server');
          }
        } catch (createError: any) {
          console.error('Create error details:', {
            status: createError.response?.status,
            statusText: createError.response?.statusText,
            data: createError.response?.data,
            message: createError.message
          });

          // Detailed error analysis
          if (createError.response?.data?.errors) {
            const errors = createError.response.data.errors;
            console.error('Validation errors:', errors);

            // Check specific field errors
            const startDateError = errors.find((e: any) => e.path === 'startDate');
            if (startDateError) {
              console.error('StartDate specific error:', startDateError);
              console.error('Value we sent:', apiValues.startDate);
            }
          }

          throw createError;
        }
      }

      resetForm();
      closeModal();
      setTimeout(() => setSuccessMessage(null), 3000);

    } catch (apiError: any) {
      console.error('API request failed:', apiError);
      setError(apiError);
    } finally {
      setSubmitting(false);
    }
  };

  // Export fermentation processes to Excel (desktop dataset).
  const handleExportToExcel = () => {
    if (!fermentations.length) return;

    const columns: ExcelColumn<FermentationProcess>[] = [
      {
        header: 'Batch',
        accessor: (f) =>
          f.wineBatch?.vineyard?.name
            ? `${f.wineBatch.vineyard.name} - ${f.wineBatch.batch_id || 'No ID'}`
            : f.wineBatch?.batch_id || 'No ID',
      },
      {
        header: 'Batch Name',
        accessor: (f) => f.wineBatch?.batch_name || '',
      },
      {
        header: 'Vessel',
        accessor: (f) =>
          f.vessel ? f.vessel.vessel_id || `#${f.vessel.id}` : '',
      },
      {
        header: 'Start Date',
        accessor: (f) => f.start_date || '',
      },
      {
        header: 'Press Date',
        accessor: (f) => f.press_date || '',
      },
      {
        header: 'Status',
        accessor: (f) => f.status,
      },
      {
        header: 'Initial Brix',
        accessor: (f) => f.initial_brix || '',
      },
    ];

    exportToExcel({
      fileName: `fermentation-${new Date().toISOString().slice(0, 10)}.xlsx`,
      sheetName: 'Fermentation',
      rows: fermentations,
      columns,
    });
  };

  // Get fermentation data with proper vineyard information
  const getUpdatedFermentation = async (fermentationId: number): Promise<FermentationProcess> => {
    try {
      const response = await api.get(`/api/fermentation/${fermentationId}`);
      // Use mapping function on the refetched data
      return mapApiDataToFermentation(response.data);
    } catch (error) {
      console.error(`Error fetching updated fermentation ${fermentationId}:`, error);
      setError('Failed to reload fermentation details after update.');
      // Return a minimal structure or re-throw to handle the error upstream
      throw error; // Re-throw the error to be caught by handleSubmit
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-success-500 text-white';
      case 'Completed':
        return 'bg-info-500 text-white';
      case 'Stuck':
        return 'bg-error-500 text-white';
      case 'Stopped':
        return 'bg-warning-500 text-white';
      default:
        return 'bg-monday-status-neutral text-white';
    }
  };

  // Helper function to get fermentation index info for batches with multiple fermentations
  const getFermentationIndexInfo = (fermentation: FermentationProcess): { index: number; total: number; label: string; color: string; tooltip: string } | null => {
    if (!fermentation.batch_id) return null;

    // Find all fermentations for this wine batch
    const fermentationsForBatch = fermentations
      .filter(f => f.batch_id === fermentation.batch_id)
      .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()); // sort by start date

    // Only show index if there are multiple fermentations for this batch
    if (fermentationsForBatch.length > 1) {
      const index = fermentationsForBatch.findIndex(f => f.id === fermentation.id);
      if (index < 0) return null;

      // Create ordinal label (1st, 2nd, 3rd, etc.)
      const ordinals = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th'];
      const label = ordinals[index] || `${index + 1}th`;

      // Color scheme - different colors for each fermentation
      const colors = [
        'bg-info-50 text-info-700 border-info-200',
        'bg-purple-100 text-purple-700 border-purple-200',
        'bg-green-100 text-green-700 border-green-200',
        'bg-orange-100 text-orange-700 border-orange-200',
        'bg-pink-100 text-pink-700 border-pink-200',
        'bg-teal-100 text-teal-700 border-teal-200',
      ];
      const color = colors[index % colors.length];

      // Tooltip with more context
      const startDate = fermentation.start_date ? new Date(fermentation.start_date).toLocaleDateString() : 'Unknown date';
      const tooltip = `${label} Fermentation - Started: ${startDate} - Status: ${fermentation.status}`;

      return { index: index + 1, total: fermentationsForBatch.length, label, color, tooltip };
    }

    return null;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fermentation Management"
        subtitle="Track and manage fermentation processes."
        variant="compact"
        actions={(
          <div className="flex space-x-3">
            <div className="relative flex items-center gap-3">
              <button
                type="button"
                onClick={handleExportToExcel}
                className="inline-flex items-center px-4 py-2 border border-monday-border text-sm font-medium rounded-md shadow-sm text-monday-text bg-white hover:bg-monday-hover"
              >
                Export to Excel
              </button>
              {!availableVessels.length && (
                <div className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 shadow-sm" role="alert" aria-live="polite">
                  <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.721-1.36 3.486 0l6.518 11.591c.75 1.335-.213 2.985-1.743 2.985H3.482c-1.53 0-2.493-1.65-1.743-2.985L8.257 3.1zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V7a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span>No available vessels.</span>
                  <Link to="/vessels" className="font-semibold underline hover:text-red-900">
                    Manage
                  </Link>
                </div>
              )}
              <PermissionButton
                resource="fermentation"
                action="create"
                onClick={() => availableVessels.length ? openModal() : null}
                className={!availableVessels.length ? '!bg-neutral-400 !cursor-not-allowed' : ''}
              >
                Start New Fermentation
              </PermissionButton>
            </div>
          </div>
        )}
      />

      {error && (
        <ErrorMessageComponent error={error} className="mt-4" />
      )}

      {successMessage && (
        <div className="mt-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
          <span className="block sm:inline">{successMessage}</span>
        </div>
      )}

      <div className="mt-8 flex flex-col">
        {/* Desktop Table View */}
        <div className="hidden sm:block">
          <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-neutral-300">
                  <thead className="bg-monday-header">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-monday-text sm:pl-6">Wine Batch</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text">Vessel</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text">Start Date</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text">Press Date</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text">Status</th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6"><span className="sr-only">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-monday-border-light bg-white">
                    {fermentations.length > 0 ? (
                      fermentations.map((fermentation) => (
                        <React.Fragment key={fermentation.id}>
                          <tr className="group hover:bg-monday-hover transition-colors duration-150">
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-monday-text sm:pl-6">
                              <div className="flex flex-col">
                                <span className="data-row-primary flex items-center gap-2">
                                  <span>
                                    {fermentation.wineBatch?.vineyard?.name ? (
                                      `${fermentation.wineBatch.vineyard.name} - ${fermentation.wineBatch?.batch_id || 'No ID'}`
                                    ) : (
                                      fermentation.wineBatch?.batch_id || 'No ID'
                                    )}
                                  </span>
                                  {(() => {
                                    const indexInfo = getFermentationIndexInfo(fermentation);
                                    return indexInfo && (
                                      <span
                                        className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium border ${indexInfo.color}`}
                                        title={indexInfo.tooltip}
                                      >
                                        {indexInfo.label}
                                      </span>
                                    );
                                  })()}
                                </span>
                                <span className="data-row-secondary">{fermentation.wineBatch?.batch_name || 'Unnamed Batch'}</span>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 data-cell-muted cursor-pointer hover:text-primary-600" onClick={() => { if (fermentation.vessel) { navigate(`/vessel/${fermentation.vessel.id}`); } }}>
                              {fermentation.vessel ? (fermentation.vessel.vessel_id || `#${fermentation.vessel.id}`) : '-'}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                              {fermentation.start_date ? new Date(fermentation.start_date).toLocaleDateString() !== "Invalid Date" ? new Date(fermentation.start_date).toLocaleDateString() : fermentation.start_date : 'Not set'}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                              {fermentation.press_date ? new Date(fermentation.press_date).toLocaleDateString() !== "Invalid Date" ? new Date(fermentation.press_date).toLocaleDateString() : fermentation.press_date : 'Not set'}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                              <span className={`inline-flex rounded px-2 text-xs font-semibold leading-5 ${getStatusClass(fermentation.status)}`}>{fermentation.status}</span>
                            </td>
                            <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                              <div className="flex items-center justify-end gap-2">
                                <ExpandChevron
                                  isExpanded={expandedFermentations.includes(fermentation.id)}
                                  onClick={(e) => { e.stopPropagation(); toggleExpandFermentation(fermentation.id); }}
                                />
                                <TableActions>
                                  <PermissionButton
                                    resource="fermentation"
                                    action="update"
                                    onClick={() => openModal(fermentation)}
                                    variant="secondary"
                                    className="!px-2.5 !py-1 !text-sm !bg-transparent !text-monday-text-muted hover:!bg-monday-hover hover:!text-monday-text !border-0 !shadow-none"
                                  >
                                    Edit
                                  </PermissionButton>
                                  {isAdmin && (
                                    <button
                                      onClick={async () => {
                                        if (!window.confirm('Are you sure you want to delete this fermentation process?')) return;
                                        try {
                                          await api.delete(`/api/fermentation/${fermentation.id}`);
                                          setFermentations(prev => prev.filter(f => f.id !== fermentation.id));
                                          setSuccessMessage('Fermentation process deleted successfully');
                                          setTimeout(() => setSuccessMessage(null), 3000);
                                        } catch (err: any) {
                                          const data = err.response?.data;
                                          if (err.response?.status === 409 && data?.dependencies) {
                                            const d = data.dependencies || {};
                                            const parts: string[] = [];
                                            if (d.fermentationMaintenance) parts.push(`${d.fermentationMaintenance} maintenance`);
                                            if (d.postFermentation) parts.push(`${d.postFermentation} post-fermentation`);
                                            setError(`Cannot delete fermentation. Delete related ${parts.join(', ')} first.`);
                                          } else {
                                            setError(err as any);
                                          }
                                        }
                                      }}
                                      className="px-2.5 py-1 text-sm font-medium text-monday-text-muted hover:text-error-600 hover:bg-error-50 rounded-md transition-colors"
                                    >
                                      Delete
                                    </button>
                                  )}
                                </TableActions>
                              </div>
                            </td>
                          </tr>
                          <ExpandableRow isExpanded={expandedFermentations.includes(fermentation.id)} colSpan={6}>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              {fermentation.initial_brix && (
                                <div className="text-sm">
                                  <span className="font-medium text-monday-text">Initial Brix: </span>
                                  <span className="text-monday-text">{fermentation.initial_brix}</span>
                                </div>
                              )}
                              {fermentation.end_date && (
                                <div className="text-sm">
                                  <span className="font-medium text-monday-text">End Date: </span>
                                  <span className="text-monday-text">{new Date(fermentation.end_date).toLocaleDateString()}</span>
                                </div>
                              )}
                              {fermentation.yeast_type && (
                                <div className="text-sm">
                                  <span className="font-medium text-monday-text">Yeast Type: </span>
                                  <span className="text-monday-text">{fermentation.yeast_type}</span>
                                </div>
                              )}
                              {fermentation.yeast_volume && (
                                <div className="text-sm">
                                  <span className="font-medium text-monday-text">Yeast Volume: </span>
                                  <span className="text-monday-text">{fermentation.yeast_volume}</span>
                                </div>
                              )}
                              {fermentation.notes && (
                                <div className="text-sm col-span-3">
                                  <span className="font-medium text-monday-text">Notes: </span>
                                  <span className="text-monday-text">{fermentation.notes}</span>
                                </div>
                              )}
                            </div>
                          </ExpandableRow>
                        </React.Fragment>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="py-4 text-center text-sm text-monday-text-muted">
                          No fermentation processes found. Click "Start New Fermentation" to create one.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        {/* Mobile Card/List View */}
        <div className="sm:hidden space-y-4">
          {fermentations.length > 0 ? (
            fermentations.map((fermentation) => (
              <div key={fermentation.id} className="bg-white shadow rounded-lg p-4 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="data-row-primary flex items-center gap-2 flex-wrap">
                      <span>{fermentation.wineBatch?.vineyard?.name ? `${fermentation.wineBatch.vineyard.name} - ${fermentation.wineBatch?.batch_id || 'No ID'}` : fermentation.wineBatch?.batch_id || 'No ID'}</span>
                      {(() => {
                        const indexInfo = getFermentationIndexInfo(fermentation);
                        return indexInfo && (
                          <span
                            className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium border ${indexInfo.color}`}
                            title={indexInfo.tooltip}
                          >
                            {indexInfo.label}
                          </span>
                        );
                      })()}
                    </div>
                    <div className="data-row-secondary">{fermentation.wineBatch?.batch_name || 'Unnamed Batch'}</div>
                  </div>
                  <span className={`inline-flex rounded px-2 text-xs font-semibold leading-5 ${getStatusClass(fermentation.status)}`}>{fermentation.status}</span>
                </div>
                <div className="flex flex-wrap gap-2 text-sm text-monday-text">
                  <div><span className="font-medium">Vessel:</span> {fermentation.vessel ? (fermentation.vessel.vessel_id || `#${fermentation.vessel.id}`) : '-'}</div>
                  <div><span className="font-medium">Start:</span> {fermentation.start_date ? new Date(fermentation.start_date).toLocaleDateString() !== "Invalid Date" ? new Date(fermentation.start_date).toLocaleDateString() : fermentation.start_date : 'Not set'}</div>
                  <div><span className="font-medium">Press:</span> {fermentation.press_date ? new Date(fermentation.press_date).toLocaleDateString() !== "Invalid Date" ? new Date(fermentation.press_date).toLocaleDateString() : fermentation.press_date : 'Not set'}</div>
                  {fermentation.yeast_type && (<div><span className="font-medium">Yeast Type:</span> {fermentation.yeast_type}</div>)}
                  {fermentation.yeast_volume && (<div><span className="font-medium">Yeast Volume:</span> {fermentation.yeast_volume}</div>)}
                </div>
                <div className="flex gap-2 mt-2">
                  <PermissionButton
                    resource="fermentation"
                    action="update"
                    onClick={() => openModal(fermentation)}
                    variant="secondary"
                    className="!flex-1 !py-2 !text-xs"
                  >
                    Edit
                  </PermissionButton>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-monday-text-muted py-8">No fermentation processes found. Click "Start New Fermentation" to create one.</div>
          )}
        </div>
      </div>

      {/* Fermentation Create/Edit Modal */}
      <MobileModal isOpen={isModalOpen} onClose={closeModal} title={editingFermentation ? 'Edit Fermentation Process' : 'Start New Fermentation Process'} size="full">
        <div className="mobile-form-container">
          <Formik
            initialValues={{
              batch_id: editingFermentation?.batch_id || (wineBatches.length > 0 ? wineBatches[0].id : 0),
              vessel_id: editingFermentation?.vessel_id || (availableVessels.length > 0 ? availableVessels[0].id : 0),
              start_date: editingFermentation?.start_date
                ? (() => {
                  console.log('Parsing start_date:', editingFermentation.start_date);
                  try {
                    const date = new Date(editingFermentation.start_date);
                    if (isNaN(date.getTime())) {
                      console.error('Invalid start_date:', editingFermentation.start_date);
                      return '';
                    }
                    const isoDate = date.toISOString().split('T')[0];
                    console.log('Formatted start_date for form:', isoDate);
                    return isoDate;
                  } catch (e) {
                    console.error('Error parsing start_date:', e);
                    return '';
                  }
                })()
                : new Date().toISOString().split('T')[0],
              end_date: editingFermentation?.end_date
                ? (() => {
                  console.log('Parsing end_date:', editingFermentation.end_date);
                  try {
                    const date = new Date(editingFermentation.end_date);
                    if (isNaN(date.getTime())) return '';
                    return date.toISOString().split('T')[0];
                  } catch (e) {
                    console.error('Error parsing end_date:', e);
                    return '';
                  }
                })()
                : '',
              status: editingFermentation?.status || 'Active',
              press_date: editingFermentation?.press_date
                ? (() => {
                  console.log('Parsing press_date:', editingFermentation.press_date);
                  try {
                    const date = new Date(editingFermentation.press_date);
                    if (isNaN(date.getTime())) return '';
                    return date.toISOString().split('T')[0];
                  } catch (e) {
                    console.error('Error parsing press_date:', e);
                    return '';
                  }
                })()
                : '',
              yeast_type: editingFermentation?.yeast_type || '',
              yeast_volume: editingFermentation?.yeast_volume || '',
              notes: editingFermentation?.notes || ''
            }}
            validationSchema={FermentationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ isSubmitting, errors, touched, values, setFieldValue }) => (
              <Form className="space-y-4">
                <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="batch_id" className="form-label">
                      Batch
                    </label>
                    <Field
                      as="select"
                      name="batch_id"
                      id="batch_id"
                      className={`mobile-form-input ${errors.batch_id && touched.batch_id ? 'border-red-500' : ''}`}
                    >
                      <option value="">Select Batch</option>
                      {wineBatches.map((batch) => (
                        <option key={batch.id} value={batch.id}>
                          {batch.vineyard?.name || 'Unknown Vineyard'} - {batch.batch_id || (batch as any).batchId || `#${batch.id}`} - {batch.batch_name || (batch as any).batchName || 'Unnamed Batch'} ({batch.grape_variety || (batch as any).grapeVariety || batch.wine_type || (batch as any).wineType || 'Unknown Type'})
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage name="batch_id" component="div" className="text-red-600 text-sm mt-1" />
                  </div>

                  <div>
                    <label htmlFor="vessel_id" className="form-label">
                      Vessel
                    </label>
                    <Field
                      as="select"
                      name="vessel_id"
                      id="vessel_id"
                      className={`mobile-form-input ${errors.vessel_id && touched.vessel_id ? 'border-red-500' : ''}`}
                      disabled={!availableVessels.length}
                    >
                      <option value="">{availableVessels.length ? 'Select Vessel' : 'No Available Vessels'}</option>
                      {availableVessels.map((vessel) => (
                        <option key={vessel.id} value={vessel.id}>
                          {vessel.vesselId || vessel.vessel_id || `#${vessel.id}`} - {vessel.vesselType || vessel.type} ({vessel.capacity}{vessel.capacityUnit || vessel.capacity_unit})
                          {vessel.status === 'Empty' ? ' [Empty]' : ''}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage name="vessel_id" component="div" className="text-red-600 text-sm mt-1" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="start_date" className="form-label">
                      Start Date
                    </label>
                    <Field type="date" name="start_date" id="start_date" className="mobile-form-input" />
                    <ErrorMessage name="start_date" component="div" className="text-red-500 text-xs mt-1" />
                  </div>
                  <div>
                    <label htmlFor="end_date" className="form-label">
                      End Date (if completed)
                    </label>
                    <Field type="date" name="end_date" id="end_date" className="mobile-form-input" />
                    <ErrorMessage name="end_date" component="div" className="text-red-500 text-xs mt-1" />
                  </div>
                </div>

                <div>
                  <label htmlFor="status" className="form-label">
                    Status
                  </label>
                  <Field as="select" name="status" id="status" className="mobile-form-input">
                    <option value="Active">Active</option>
                    <option value="Completed">Completed</option>
                    <option value="Stuck">Stuck</option>
                    <option value="Stopped">Stopped</option>
                  </Field>
                  <ErrorMessage name="status" component="div" className="text-red-500 text-xs mt-1" />
                </div>

                <div>
                  <label htmlFor="press_date" className="form-label">
                    Press Date (for red wines)
                  </label>
                  <Field type="date" name="press_date" id="press_date" className="mobile-form-input" />
                  <ErrorMessage name="press_date" component="div" className="text-red-500 text-xs mt-1" />
                </div>

                <div>
                  <label htmlFor="yeast_type" className="form-label">
                    Yeast Type
                  </label>
                  <Field type="text" name="yeast_type" id="yeast_type" className="mobile-form-input" placeholder="e.g., Saccharomyces cerevisiae, Wild yeast" />
                  <ErrorMessage name="yeast_type" component="div" className="text-red-500 text-xs mt-1" />
                </div>

                <div>
                  <label htmlFor="yeast_volume" className="form-label">
                    Yeast Volume
                  </label>
                  <Field type="text" name="yeast_volume" id="yeast_volume" className="mobile-form-input" placeholder="e.g., 25g, 1 packet, 500ml starter" />
                  <ErrorMessage name="yeast_volume" component="div" className="text-red-500 text-xs mt-1" />
                </div>

                <div>
                  <label htmlFor="notes" className="form-label">
                    Notes
                  </label>
                  <Field as="textarea" name="notes" id="notes" rows={3} className="mobile-form-input resize-none" placeholder="Additional notes about the fermentation process" />
                  <ErrorMessage name="notes" component="div" className="text-red-500 text-xs mt-1" />
                </div>

                <div className="sticky bottom-0 -mx-6 md:-mx-8 mt-5 px-6 py-4 md:px-8 border-t border-monday-border-light bg-white flex items-center justify-end gap-3">
                  <button type="button" onClick={closeModal} className="mobile-btn mobile-btn-secondary w-full sm:w-auto">
                    Cancel
                  </button>
                  <button type="submit" disabled={isSubmitting || !availableVessels.length} className={`mobile-btn mobile-btn-primary w-full sm:w-auto ${!availableVessels.length ? 'opacity-60 cursor-not-allowed' : ''}`}>
                    {isSubmitting ? 'Saving...' : !availableVessels.length ? 'No Available Vessels' : (editingFermentation ? 'Update' : 'Create')}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </MobileModal>
    </div>
  );
};

export default Fermentation;
